import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  apiCreateEmptyTemplate,
  apiCreateTemplateFromText,
  apiDeleteTemplate,
  apiFetchTemplateById,
  apiFetchTemplates,
  apiUpdateTemplate,
} from '../../../../../routes/doctor/templates';

// --- TYPE DEFINITIONS ---
// This defines the structure of a single section within a template.
export interface TemplateSection {
  id: string; // Using a client-side generated ID for key props
  title: string;
  description: string;
  exampleText: string;
}

// This is the main Template object structure.
export interface Template {
  _id: string;
  title: string;
  language: string;
  category: string;
  status: 'Draft' | 'Completed';
  sections: TemplateSection[];
  createdAt?: string;
  updatedAt?: string;
}

// --- SLICE STATE ---
interface TemplatesState {
  templates: Template[];
  activeTemplate: Template | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: TemplatesState = {
  templates: [],
  activeTemplate: null,
  status: 'idle',
  error: null,
};

// --- ASYNC THUNKS (for API calls) ---

// Fetches the list of all templates for the library view
export const fetchTemplates = createAsyncThunk('templates/fetchTemplates', async () => {
  const response = await apiFetchTemplates();
  return response.data; // Assuming the API returns the array of templates
});

// Fetches a single, complete template for the editor view
export const fetchTemplateById = createAsyncThunk('templates/fetchTemplateById', async (templateId: string) => {
  const response = await apiFetchTemplateById(templateId);
  return response.data;
});

// Creates a new template (handles both empty and AI-parsed)
export const addNewTemplate = createAsyncThunk(
  'templates/addNewTemplate',
  async (initialTemplate: { title: string; language: string; pastedText?: string }) => {
    let response;
    if (initialTemplate.pastedText) {
      response = await apiCreateTemplateFromText(initialTemplate.title, initialTemplate.language, initialTemplate.pastedText);
    } else {
      response = await apiCreateEmptyTemplate(initialTemplate.title, initialTemplate.language);
    }
    return response.data;
  }
);

// Updates an existing template
export const updateExistingTemplate = createAsyncThunk('templates/updateTemplate', async (template: Template) => {
  const response = await apiUpdateTemplate(template);
  return response.data;
});

// Deletes a template from the library
export const deleteExistingTemplate = createAsyncThunk('templates/deleteTemplate', async (templateId: string) => {
  await apiDeleteTemplate(templateId);
  return templateId; // Return the ID to remove it from the state
});

// --- SLICE DEFINITION ---
const templatesSlice = createSlice({
  name: 'templates',
  initialState,
  reducers: {
    // Used to clear the active template when leaving the editor page
    clearActiveTemplate: (state) => {
      state.activeTemplate = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All Templates
      .addCase(fetchTemplates.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchTemplates.fulfilled, (state, action: PayloadAction<Template[]>) => {
        state.status = 'succeeded';
        state.templates = action.payload;
      })
      .addCase(fetchTemplates.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch templates';
      })
      // Fetch Single Template by ID
      .addCase(fetchTemplateById.pending, (state) => {
        state.status = 'loading';
        state.activeTemplate = null;
      })
      .addCase(fetchTemplateById.fulfilled, (state, action: PayloadAction<Template>) => {
        state.status = 'succeeded';
        state.activeTemplate = action.payload;
      })
      .addCase(fetchTemplateById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch template';
      })
      // Add New Template
      .addCase(addNewTemplate.fulfilled, (state, action: PayloadAction<Template>) => {
        state.templates.unshift(action.payload); // Add to the beginning of the list
      })
      // Update Existing Template
      .addCase(updateExistingTemplate.fulfilled, (state, action: PayloadAction<Template>) => {
        const updatedTemplate = action.payload;
        const existingIndex = state.templates.findIndex((t) => t._id === updatedTemplate._id);
        if (existingIndex !== -1) {
          state.templates[existingIndex] = updatedTemplate;
        }
        state.activeTemplate = updatedTemplate; // Also update the active one if it's being edited
        state.status = 'succeeded';
      })
      .addCase(updateExistingTemplate.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateExistingTemplate.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to save template';
      })
      // Delete Existing Template
      .addCase(deleteExistingTemplate.fulfilled, (state, action: PayloadAction<string>) => {
        state.templates = state.templates.filter((t) => t._id !== action.payload);
      });
  },
});

export const { clearActiveTemplate } = templatesSlice.actions;

export default templatesSlice.reducer;